import {
  Color,
  DynamicDrawUsage,
  InstancedBufferGeometry,
  InstancedInterleavedBuffer,
  InterleavedBufferAttribute,
} from "three";
import { PathPoint, toGlobeCoordinates } from "./orbitGeometry";

const START_COLOR = new Color("#a7c4ce");
const END_COLOR = new Color("#304c5b");

export const updateOrbitLineGeometry = (
  geometry: InstancedBufferGeometry,
  points: PathPoint[],
  radius: number,
) => {
  const segmentCount = Math.max(0, points.length - 1);
  geometry.instanceCount = segmentCount;
  if (!segmentCount) return;

  let start = geometry.getAttribute("instanceStart") as
    | InterleavedBufferAttribute
    | undefined;
  let end = geometry.getAttribute("instanceEnd") as InterleavedBufferAttribute;

  if (start?.count !== segmentCount) {
    // Release the previous GPU buffers if a path changes resolution.
    if (start) geometry.dispose();
    const positions = new InstancedInterleavedBuffer(
      new Float32Array(segmentCount * 6),
      6,
      1,
    );
    positions.setUsage(DynamicDrawUsage);
    start = new InterleavedBufferAttribute(positions, 3, 0);
    end = new InterleavedBufferAttribute(positions, 3, 3);
    geometry.setAttribute("instanceStart", start);
    geometry.setAttribute("instanceEnd", end);

    const colors = new Float32Array(segmentCount * 6);
    const color = new Color();
    for (let index = 0; index <= segmentCount; index++) {
      color.copy(START_COLOR).lerp(END_COLOR, index / segmentCount);
      if (index < segmentCount) color.toArray(colors, index * 6);
      if (index > 0) color.toArray(colors, (index - 1) * 6 + 3);
    }
    const colorBuffer = new InstancedInterleavedBuffer(colors, 6, 1);
    geometry.setAttribute(
      "instanceColorStart",
      new InterleavedBufferAttribute(colorBuffer, 3, 0),
    );
    geometry.setAttribute(
      "instanceColorEnd",
      new InterleavedBufferAttribute(colorBuffer, 3, 3),
    );
  }

  points.forEach((point, index) => {
    const [x, y, z] = toGlobeCoordinates(point, radius);
    if (index < segmentCount) start!.setXYZ(index, x, y, z);
    if (index > 0) end.setXYZ(index - 1, x, y, z);
  });
  start.data.needsUpdate = true;
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
};
